import 'rc-tree/assets/index.css'

import { Icon } from '@iconify/react'
import { Computed, useObservable } from '@legendapp/state/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useLoaderData } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import Tree from 'rc-tree'
import React, { Key, useState } from 'react'
import { toast } from 'react-toastify'
import { ulid } from 'ulidx'

import { addProductPrivilege, deleteProductPrivileges, fetchProductPrivilegeList, updateProductPrivilege } from '~features/product-privileges/api'
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  DialogComponent,
  IconButton,
  Input,
  Label,
} from '~in-app/components'
import Dropdown from '~in-app/components/Dropdown'
import { Option } from '~in-app/components/MultiSelectDropdown'
import { auth$ } from '~in-app/legend-state'
import { fetchProductList } from '~in-app/shareable/index.api'
import { productPrivilegeValidation } from '~in-app/validation/product-privileges.validation'

export const Route = createFileRoute('/console/products/privileges/list')({
  component: ProductPrivilegePage,
  loader: async ({ abortController }) => {
    let productIds: Option[] = []

    const privilegeValue = auth$.privileges.get()
    // Ensure that the final return contains the fetched values.

    const productList = await fetchProductList({
      abortCtrl: abortController,
    })
    productIds = productList?.list.map((item: any) => ({
      value: item.id,
      label: item.name,
    }))

    return { privilegeValue, productIds }
  },
  staticData: {
    privileges: ['0.3.8.8'],
  },
})

function ProductPrivilegePage() {
  const onDrop = (info: {
    node: { key: any, pos: string }
    dragNode: { key: any }
    dropPosition: number
  }) => {
    const { key: dropKey, pos } = info.node
    const { key: dragKey } = info.dragNode
    const dropPosition = info.dropPosition - Number(pos.split('-').pop())

    const loop = (
      data: any[],
      key: any,
      callback: (item: any, index: number, arr: any[]) => void,
    ) => {
      for (let i = 0; i < data.length; i++) {
        const item = data[i]
        if (item.key === key) {
          callback(item, i, data)
          return
        }
        if (item.children) {
          loop(item.children, key, callback)
        }
      }
    }

    const updateChildrenPos = (children: any[], parentPos: string) => {
      children.forEach((child, index) => {
        child.pos = `${parentPos}-${index + 1}`
        if (child.children) {
          updateChildrenPos(child.children, child.pos)
        }
      })
    }

    const data = [...formControl.getFieldValue('list')]

    // Find and remove dragObj from its original position
    let dragObj: any
    loop(data, dragKey, (item, index, arr) => {
      dragObj = { ...item } // Clone the item to avoid direct reference issues
      arr.splice(index, 1) // Remove the dragged item
    })

    if (dropPosition === 0) {
      // Drop on content
      loop(data, dropKey, (item) => {
        item.children = item.children || []
        dragObj.parentId = dropKey // Set the parentId before adding
        dragObj.pos = `${item.pos}-${item.children.length + 1}` // Set the new pos
        item.children.unshift(dragObj) // Add dragObj to the beginning
        updateChildrenPos(item.children, item.pos) // Update positions of all children
      })
    }
    else {
      // Drop in the gap (before or after)
      let targetArr: any[] = [] // Initialize to avoid "used before assigned" error
      let targetIndex = -1 // Initialize to avoid "used before assigned" error
      loop(data, dropKey, (_, index, arr) => {
        targetArr = arr
        targetIndex = index
      })

      dragObj.parentId = targetArr[targetIndex].parentId // Set the parentId before insertion
      dragObj.pos = `${targetArr[targetIndex].pos}-${dropPosition === -1 ? targetIndex : targetIndex + 1}` // Set the new pos

      if (dropPosition === -1) {
        targetArr.splice(targetIndex, 0, dragObj) // Insert before
      }
      else {
        targetArr.splice(targetIndex + 1, 0, dragObj) // Insert after
      }
      updateChildrenPos(targetArr, targetArr[targetIndex].parentId) // Update positions of all children
    }
    formControl.setFieldValue('list', data)
    formControl.validate('change')
    // Ensure the keys are expanded after drop
    const expandedKeys = new Set(state$.expandedKeys.get())
    expandedKeys.add(dropKey)
    expandedKeys.add(dragKey)
    state$.expandedKeys.set(Array.from(expandedKeys))
  }

  const onExpand = (expandedKeys: any) => {
    state$.expandedKeys.set(expandedKeys)
  }
  const { privilegeValue, productIds } = useLoaderData({
    from: '/console/products/privileges/list',
  })
  const state$ = useObservable({
    expandedKeys: [] as Key[],
    productIds: '',
    isSubmit: false,
    isOpenDeletePrivileges: false,
    deletePrivilege: undefined as TreeNode | undefined,
  })
  const transformItem = (item: { [x: string]: any, children: any }) => {
    // Prepare the new structure for the item
    const { children, ...rest } = item // Separate children from the rest of the item
    const newItem: any = {
      ...rest,
      id: rest.id,
      name: rest.name,
      key: rest.key,
      productId: state$.productIds.get(),
      parentId: rest.parentId,
      children: children ? children.map(transformItem) : [], // Recursively transform children
      code: rest.code,
      multiTenant: rest.multiTenant,
      multiTenantHost: rest.multiTenantHost,
    }

    return newItem
  }
  const updateProductPrivilegeFunc = () => {
    const mutation = useMutation({
      mutationFn: updateProductPrivilege,
      onSuccess: (response) => {
        const updateGData = (data: any[], code: string, updatedFields: any): any[] => {
          return data.map((item: any): any => {
            if (item.code === code) {
              return {
                ...item,
                ...updatedFields,
                key: updatedFields.id,
                isNew: false,
              }
            }
            if (item.children) {
              return {
                ...item,
                children: updateGData(item.children, code, updatedFields),
                isNew: false,
              }
            }
            return item
          })
        }

        const { code, version, id, name, parent, multiTenant, multiTenantHost, active, onPremise } = response[0]
        const parentId = parent?.id
        const updatedFields = {
          version,
          id,
          name,
          parentId,
          code,
          multiTenant,
          multiTenantHost,
          active,
          onPremise,
        }

        const beforeUpdatelist = formControl.getFieldValue('list')
        formControl.setFieldValue('list', updateGData(beforeUpdatelist, code, updatedFields))
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }

  // const addProductPrivilegeFunc = () => {
  //   const mutation = useMutation({
  //     mutationFn: addProductPrivilege,
  //     onSuccess: (response) => {
  //       const updateGData = (data: any[], code: string, updatedFields: any): any[] => {
  //         return data.map((item: any): any => {
  //           if (item.code === code) {
  //             return {
  //               ...item,
  //               ...updatedFields,
  //               key: updatedFields.id,
  //               isNew: false,
  //             }
  //           }
  //           if (item.children) {
  //             return {
  //               ...item,
  //               children: updateGData(item.children, code, updatedFields),
  //               isNew: false,
  //             }
  //           }
  //           return item
  //         })
  //       }

  //       const { code, version, id, name, parent, multiTenant, multiTenantHost, active, onPremise } = response[0]
  //       const parentId = parent?.id
  //       const updatedFields = {
  //         version,
  //         id,
  //         name,
  //         parentId,
  //         multiTenant,
  //         multiTenantHost,
  //         active,
  //         onPremise,
  //       }
  //       formControl.setFieldValue('list', updateGData(formControl.getFieldValue('list'), code, updatedFields))
  //     },
  //     onError: (error: Error) => {
  //       console.error('Error occurred:', error)
  //     },
  //   })

  //   return mutation
  // }
  const flattenList = (data: any[]): any[] => {
    return data.flatMap((item) => {
      return item.children && item.children.length > 0
        ? [item, ...flattenList(item.children)]
        : [item]
    })
  }
  const uniqueCodeListCheck = (code: string, data: any[]): boolean => {
    if (code === '') return true // Ignore empty code
    const flattenList = (data: any[]): any[] => {
      return data.flatMap((item) => {
        return item.children && item.children.length > 0
          ? [item, ...flattenList(item.children)]
          : [item]
      })
    }
    const allCodes = flattenList(data).map(item => item.code)
    return allCodes.filter(c => c === code).length === 1
  }
  const updateProductPrivilegeMutation = updateProductPrivilegeFunc()
  const updateGData = (data: any[], code: string, updatedFields: any): any[] => {
    return data.map((item: any): any => {
      if (item.code === code) {
        return {
          ...item,
          ...updatedFields,
          key: updatedFields.id,
          oldKey: item.key,
          isNew: false,
        }
      }
      if (item.children) {
        return {
          ...item,
          children: updateGData(item.children, code, updatedFields),
          isNew: false,
        }
      }
      return item
    })
  }
  // const addProductPrivilegeMutation = addProductPrivilegeFunc()
  const handleSaveProductPrivilege = (value: any) => {
    const submitValue = formControl.getFieldValue('list').map(transformItem) // Call the helper function

    // Validation function to check if all items are valid
    const isValidItem = (item: any): boolean => {
      const isNameValid = item.name.trim() !== '' && item.name.length <= 50
      const isCodeValid = item.code.trim() !== '' && item.code.length <= 24
      const isCodeUnique = uniqueCodeListCheck(item.code, formControl.getFieldValue('list'))
      const areChildrenValid = item.children ? item.children.every(isValidItem) : true
      const parentCode = item.parentId ? flattenList(submitValue).find(parent => parent.key === item.parentId)?.code : undefined
      const isCodeStructureValid = item.parentId === undefined || item.parentId === '' || (item.code.startsWith(parentCode + '.')
        && item.code.split('.').length === (parentCode.split('.').length + 1))

      return isNameValid && isCodeValid && isCodeUnique && areChildrenValid && isCodeStructureValid
    }

    // Check if all items are valid before proceeding
    const allItemsValid = submitValue.every(isValidItem)
    if (!allItemsValid) {
      toast.error('Some items have invalid data. Please correct them before saving.', {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 5000,
      })
      return
    }

    const submitItemWithChildren = async (item: any) => {
      if (item.isNew) {
        const newObject = { ...item }
        if (item.parentId) {
          const parentItem = flattenList(formControl.getFieldValue('list')).find(
            parent => parent.oldKey === item.parentId,
          )
          if (parentItem) {
            newObject.parentId = parentItem.id
          }
        }

        try {
          const response = await addProductPrivilege({ value: [newObject] })

          const { code, version, id, name, parent, multiTenant, multiTenantHost, active, onPremise } = response[0]
          const parentId = parent?.id
          const updatedFields = {
            version,
            id,
            name,
            parentId,
            code,
            multiTenant,
            multiTenantHost,
            active,
            onPremise,
            isNew: false,
          }

          // Wait for the state update to complete
          await new Promise<void>((resolve) => {
            const updatedList = updateGData(formControl.getFieldValue('list'), code, updatedFields)

            formControl.setFieldValue('list', updatedList)
            // Use state$.expandedKeys.set to set expanded keys for the newly added item
            state$.expandedKeys.set([...state$.expandedKeys.get(), updatedFields.id])
            resolve()
          })
        }
        catch (err) {
          console.error('Error adding product privilege:', err)
          return // Abort further processing if an error occurs
        }
      }

      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          await submitItemWithChildren(child)
        }
      }
    };

    (async () => {
      state$.isSubmit.set(true) // Set isSubmit to false at the start
      try {
        for (const item of submitValue) {
          await submitItemWithChildren(item)
        }
        // Update the flattenList with the updated data
        const updatedFlattenList = flattenList(formControl.getFieldValue('list')).map((item: any) => ({
          ...item,
          productId: state$.productIds.get(),
        }))
        await new Promise<void>((resolve) => {
          updateProductPrivilegeMutation.mutate(
            { value: updatedFlattenList },
            {
              onSuccess: (response) => {
                response.map((item: any) => {
                  const { code, version, id, name, parent, multiTenant, multiTenantHost, active, onPremise } = item
                  const parentId = parent?.id
                  const updatedFields = {
                    version,
                    id,
                    name,
                    parentId,
                    code,
                    multiTenant,
                    multiTenantHost,
                    active,
                    onPremise,
                    isNew: false,
                  }
                  const updatedList = updateGData(formControl.getFieldValue('list'), code, updatedFields)

                  formControl.setFieldValue('list', updatedList)
                  setEditableItems((prev: any) => ({
                    ...prev,
                    [id]: false,
                  }))
                })
                resolve() // Call resolve after the mutation succeeds
              },
              onError: (err: any) => {
                console.error('Error updating product privilege:', err)
                resolve() // Optionally resolve here to prevent blocking, even on error
              },
            },
          )
        })
        toast.success('All product privileges saved successfully')
      }
      catch (err) {
        toast.error(`${err}`)
      }
      finally {
        state$.isSubmit.set(false) // Set isSubmit to true after the process completes
      }
    })()
  }
  const [editableItems, setEditableItems] = useState<any>({}) // To track which items are editable
  const [editingTitle, setEditingTitle] = useState<{ [key: string]: string }>(
    {},
  )
  const [editingCode, setEditingCode] = useState<{ [key: string]: string }>(
    {},
  )
  const [multiTenant, setMultiTenant] = useState<{ [key: string]: boolean }>(
    {},
  )
  const [multiTenantHost, setMultiTenantHost] = useState<{ [key: string]: boolean }>(
    {},
  )
  // Toggle edit state
  const handleEditToggle = (id: string) => {
    setEditableItems((prev: { [x: string]: any }) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }
  const updateTitleById = (
    data: any[],
    id: string,
    newTitle: string,
  ): any[] => {
    return data.map((item) => {
      if (item.key === id) {
        return { ...item, name: newTitle } // Update the title if the key matches
      }

      // If there are children, recursively update them
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateTitleById(item.children, id, newTitle),
        }
      }

      return item // Return item as is if no updates are needed
    })
  }
  const updateCodeById = (
    data: any[],
    id: string,
    newCode: string,
  ): any[] => {
    return data.map((item) => {
      if (item.key === id) {
        return { ...item, code: newCode } // Update the title if the key matches
      }

      // If there are children, recursively update them
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateCodeById(item.children, id, newCode),
        }
      }

      return item // Return item as is if no updates are needed
    })
  }
  const formOpts = formOptions({
    defaultValues: {
      productId: '',
      list: [] as any[],
      defaultValue: '',
      remarks: '',
      desp: '',
      version: 0,
      code: '',
      name: '',
      active: true,
      priviledgeIds: [],
      priviledges: '',
      type: '',
      current: false,
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: productPrivilegeValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks
      try {
        // Validate the value before saving and log the result
        handleSaveProductPrivilege(value)
      }
      catch (error) {
        console.error('Add Features failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })
  const formControl = useForm(formOpts)
  const queryClient = useQueryClient()
  function buildPrivilegeTree(privileges: any[]) {
    const privilegeMap = new Map()

    // Step 1: Initialize all privileges in a map
    privileges.forEach((privilege) => {
      privilegeMap.set(privilege.key, {
        ...privilege,
        children: [],
        isExpanded: true,
        isChecked: true,
        pos: '', // Initialize pos field
      })
    })

    // Step 2: Link children to their parents
    privileges.forEach((privilege) => {
      if (privilege.parentId) {
        const parentPrivilege = Array.from(privilegeMap.values()).find(
          item => item.id === privilege.parentId,
        )
        if (parentPrivilege) {
          parentPrivilege.children.push(privilegeMap.get(privilege.key))
          // Sort children by value in ascending order
          parentPrivilege.children.sort(
            (a: { key: string }, b: { key: any }) =>
              a.key.localeCompare(b.key, undefined, { numeric: true }),
          )
        }
      }
    })

    // Step 3: Assign hierarchical positions
    const assignPositions = (nodes: any[], prefix: string) => {
      nodes.forEach((node, index) => {
        node.pos = prefix ? `${prefix}-${index}` : `${index}`
        if (node.children && node.children.length > 0) {
          assignPositions(node.children, node.pos)
        }
      })
    }

    const filteredTreeData = Array.from(privilegeMap.values()).filter(
      (privilege: any) => !privilege.parentId,
    )

    // Assign positions starting from the root level
    assignPositions(filteredTreeData, '')

    // Step 4: Return the root-level privileges
    return filteredTreeData
  }

  const { data: productPrivilegeList = [] } = useQuery<TreeNode[]>({
    queryKey: ['add-product-privileges-listing'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (state$.productIds.get() === '') {
        return [] as TreeNode[]
      }
      const fetchAllPrivileges = async (
        page = 0,
        accumulatedPrivileges: any[] = [],
      ): Promise<any[]> => {
        const response = await fetchProductPrivilegeList({
          query: { productIds: state$.productIds.get(), page, size: 100 },
        })
        if (response) {
          const newPrivileges = accumulatedPrivileges.concat(response.list)
          if (page + 1 < response.totalPage) {
            return fetchAllPrivileges(page + 1, newPrivileges)
          }
          return newPrivileges
        }
        return accumulatedPrivileges
      }

      const allPrivileges = await fetchAllPrivileges()
      const privilegeList = allPrivileges.map((item: any) => ({
        ...item,
        name: item.name,
        key: item.id,
        parentId: item?.parent?.id,
      }))
      const productResponse = buildPrivilegeTree(privilegeList)
      const getAllKeys = (nodes: any[]): string[] => {
        let keys: string[] = []
        nodes.forEach((node) => {
          keys.push(node.key)
          if (node.children && node.children.length > 0) {
            keys = keys.concat(getAllKeys(node.children))
          }
        })
        return keys
      }
      state$.expandedKeys.set(getAllKeys(productResponse))
      const sortPrivileges = (privileges: any[]): any[] => {
        return privileges
          .map(privilege => ({
            ...privilege,
            children: privilege.children ? sortPrivileges(privilege.children) : [],
          }))
          .sort((a, b) => a.code.localeCompare(b.code))
      }
      formControl.setFieldValue('list', sortPrivileges(productResponse))
      return productResponse
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // Update title and gData
  const handleInputChange = (id: string, value: string) => {
    // Update editingTitle for live typing effect
    setEditingTitle(prev => ({ ...prev, [id]: value }))

    // Update gData with the new title
    formControl.setFieldValue('list', (prevGData: any[]) => updateTitleById(prevGData, id, value))
  }

  const handleInputBlur = (id: string) => {
    // Save the title to state$.gData when input loses focus
    const currentIndex = formControl.getFieldValue('list').findIndex(
      (item: { key: string }) => item.key === id,
    )
    if (currentIndex !== -1) {
      const newState = [...formControl.getFieldValue('list')]
      newState[currentIndex].name = editingTitle[id] // Update the title in the state
      formControl.setFieldValue('list', newState) // Update the global state
    }
    // Toggle edit mode off after saving
  }
  const handleInputCodeChange = (id: string, value: string) => {
    // Update editingCode for live typing effect
    setEditingCode(prev => ({ ...prev, [id]: value }))

    // Update gData with the new code
    formControl.setFieldValue('list', (prevGData: any[]) => updateCodeById(prevGData, id, value))
  }

  const handleInputCodeBlur = (id: string) => {
    // Save the code to state$.gData when input loses focus
    const currentIndex = formControl.getFieldValue('list').findIndex(
      (item: { key: string }) => item.key === id,
    )
    if (currentIndex !== -1) {
      const newState = [...formControl.getFieldValue('list')]
      newState[currentIndex].code = editingCode[id] // Update the code in the state
      formControl.setFieldValue('list', newState) // Update the global state
    }
    // Toggle edit mode off after saving
  }

  type TreeNode = {
    key: string
    name: string
    isNew: boolean
    id: string
    children: TreeNode[]
    code: string
    pos: string
    parentId: string
    multiTenant: boolean
    multiTenantHost: boolean
  }

  const removeNode = (data: any[], keyToRemove: string): any[] => {
    return data.reduce((accumulator, current) => {
      // If the current item's key matches the key to remove
      if (current.key === keyToRemove) {
        // Skip adding the current node and its children to the accumulator to effectively delete them
        return accumulator
      }

      // If the current item has children, call removeNode recursively
      if (current.children) {
        current.children = removeNode(current.children, keyToRemove)
      }

      // Add the current item to the accumulator
      accumulator.push(current)
      return accumulator
    }, [])
  }

  // Example of handleDelete using the updated removeNode
  const handleDelete = React.useCallback(
    (node: TreeNode) => {
      if (node.isNew) {
        const updatedData = removeNode(formControl.getFieldValue('list'), node.key)
        formControl.setFieldValue('list', updatedData)
      }
      else {
        // Call API to delete the node and its children

        const updatedData = removeNode(formControl.getFieldValue('list'), node.key)
        const idsToDelete: string[] = [node.id, ...flattenList(node.children).map((child: any) => child.id)]

        const reverseList = idsToDelete.reverse()
        deleteProductPrivileges({
          ids: reverseList,
        }).then(() => {
          formControl.setFieldValue('list', updatedData)
          toast.success('Privileges deleted successfully', { hideProgressBar: false, autoClose: 1500 })
        }).catch((error: any) => {
          const parsedData = JSON.parse(error.message)
          toast.error(`Error: ${parsedData.message}`, { hideProgressBar: false, autoClose: 1500 })
        })
      }
    },
    [formControl],
  )

  const handleMultiTenantChange = (id: string, value: boolean, isHost: boolean = false) => {
    const property = isHost ? 'multiTenantHost' : 'multiTenant'
    const setState = isHost ? setMultiTenantHost : setMultiTenant

    setState(prev => ({ ...prev, [id]: value }))
    formControl.setFieldValue('list', (prevGData: any[]) => updateProperty(prevGData, id, property, value))
  }

  const updateProperty = (data: any[], key: string, property: 'multiTenant' | 'multiTenantHost', checked: boolean): any[] => {
    return data.map((item) => {
      if (item.key === key) {
        return { ...item, [property]: checked }
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: updateProperty(item.children, key, property, checked),
        }
      }
      return item
    })
  }

  const renderTitle = React.useCallback(
    (node: TreeNode): React.ReactNode => {
      const { key, name, isNew, code, pos, multiTenant, multiTenantHost, parentId, children } = node
      // Validation logic
      const isNameEmpty = name && name.trim() !== ''
      const isNameValid = name && name.length <= 50
      const parentCode = flattenList(formControl.getFieldValue('list')).find(item => item.key === parentId)?.code || ''
      const isCodeValid = code && code.trim() !== ''
      const isCodeMaxLengthValid = code && code.length <= 24
      const isCodeStructureValid = parentId === undefined || parentId === '' || (code.startsWith(parentCode + '.')
        && code.split('.').length === (parentCode.split('.').length + 1))
      const nameErrorMessage = !isNameValid ? 'Name must be 50 characters or less.' : ''
      const nameEmptyErrorMessage = !isNameEmpty ? 'Name is required.' : ''
      const codeErrorMessage = !isCodeValid ? 'Code is required.' : ''
      const codeMaxLengthErrorMessage = !isCodeMaxLengthValid ? 'Code must be 24 characters or less.' : ''

      const codeStructureErrorMessage = 'Code must follow the correct hierarchy.'

      const isCodeUnique = uniqueCodeListCheck(code, formControl.getFieldValue('list'))
      const codeUniqueErrorMessage = 'Code must be unique.'
      return (
        <div
          className="flex flex-col"
          id={key}
        >
          <div className="flex items-center">
            {
              editableItems[key]
                ? (
                    <>
                      <div className="flex w-full">
                        <div
                          className="relative flex-1 items-center"
                          style={{ width: '650px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center' }}
                        >
                          <div className="flex flex-col">

                            <div className="flex items-center">
                              <Label className="pr-2">Name: </Label>

                              <Input
                                className="h-8 flex-1 pl-2"
                                id={`list.${key}.name`}
                                style={{ width: '600px' }}
                                type="text"
                                value={name}
                                onBlur={() => handleInputBlur(key)}
                                onChange={e => handleInputChange(key, e.target.value)}
                              />
                            </div>

                            {
                              (!isNameEmpty || !isNameValid) && (
                                <span className="pl-4 text-xs text-red-500">
                                  {(!isNameEmpty && nameEmptyErrorMessage) || (!isNameValid && nameErrorMessage)}
                                </span>
                              )
                            }
                          </div>
                        </div>

                        <span
                          aria-hidden="true"
                          className="rc-tree-indent"
                        >
                          {
                            (() => {
                              const findLongestPos = (data: any) => {
                                let max = 0
                                const loop = (items: any) => {
                                  items.forEach((item: any) => {
                                    const posLength = item.pos.split('-').length
                                    if (posLength > max) {
                                      max = posLength
                                    }
                                    if (item.children) {
                                      loop(item.children)
                                    }
                                  })
                                }
                                loop(data)
                                return max
                              }

                              const longestPos = findLongestPos(formControl.getFieldValue('list'))
                              const dotCount = pos.split('-').length - 1
                              const repeatCount = longestPos - dotCount

                              return Array.from({ length: repeatCount }, (_, index) => (
                                <span
                                  key={index}
                                  className="rc-tree-indent-unit"
                                />
                              ))
                            })()
                          }
                        </span>

                        <div
                          className="relative flex-1 p-2"
                          style={{ width: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center', fontSize: '0.875rem' }}
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center">
                              <Label className="pl-4 pr-2">Code: </Label>

                              <Input
                                className="h-8 flex-1 pl-2"
                                id={`list.${key}.code`}
                                type="text"
                                value={code}
                                onBlur={() => handleInputCodeBlur(key)}
                                onChange={e => handleInputCodeChange(key, e.target.value)}
                              />
                            </div>

                            {
                              (!isCodeValid || !isCodeStructureValid || !isCodeUnique || !isCodeMaxLengthValid) && (
                                <span className="pl-4 text-xs text-red-500">
                                  {(!isCodeValid && codeErrorMessage) || (!isCodeStructureValid && codeStructureErrorMessage) || (!isCodeUnique && codeUniqueErrorMessage) || (!isCodeMaxLengthValid && codeMaxLengthErrorMessage)}
                                </span>
                              )
                            }
                          </div>
                        </div>

                        <div
                          className="relative flex-1"
                          style={{ width: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center', fontSize: '0.875rem' }}
                        >
                          <div className="flex flex-col">
                            <div className="mr-8 flex items-center justify-center">
                              <Label htmlFor={`list.${key}.multiTenant`}>
                                Multi Tenant
                              </Label>

                              <Input
                                checked={multiTenant}
                                className="h-8"
                                id={`list.${key}.multiTenant`}
                                style={{ marginLeft: '5px', width: '15px' }}
                                type="checkbox"
                                onChange={
                                  e => handleMultiTenantChange(key, e.target.checked, false)
                                }
                              />
                            </div>
                          </div>
                        </div>

                        <div
                          className="relative flex-1"
                          style={{ width: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center', fontSize: '0.875rem' }}
                        >
                          <div className="flex flex-col">
                            <div className="flex items-center justify-start">
                              <Label htmlFor={`list.${key}.multiTenantHost`}>
                                Multi Tenant Host
                              </Label>

                              <Input
                                checked={multiTenantHost}
                                className="h-8"
                                id={`list.${key}.multiTenantHost`}
                                style={{ marginLeft: '5px', width: '15px' }}
                                type="checkbox"
                                onChange={
                                  e => handleMultiTenantChange(key, e.target.checked, true)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                : (
                    <div className="flex w-full">
                      <div
                        className="relative"
                        style={{ maxWidth: '450px', minWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center', fontSize: '0.875rem' }}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <Label className="mx-4">Name: </Label>
                            {name}
                          </div>

                          {
                            (!isNameEmpty || !isNameValid) && (
                              <span className="pl-4 text-xs text-red-500">
                                {(!isNameEmpty && nameEmptyErrorMessage) || (!isNameValid && nameErrorMessage)}
                              </span>
                            )
                          }
                        </div>
                      </div>

                      <span
                        aria-hidden="true"
                        className="rc-tree-indent"
                      >
                        {
                          (() => {
                            const findLongestPos = (data: any) => {
                              let max = 0
                              const loop = (items: any) => {
                                items.forEach((item: any) => {
                                  const posLength = item.pos.split('-').length
                                  if (posLength > max) {
                                    max = posLength
                                  }
                                  if (item.children) {
                                    loop(item.children)
                                  }
                                })
                              }
                              loop(data)
                              return max
                            }

                            const longestPos = findLongestPos(formControl.getFieldValue('list'))
                            const dotCount = pos.split('-').length - 1
                            const repeatCount = longestPos - dotCount

                            return Array.from({ length: repeatCount }, (_, index) => (
                              <span
                                key={index}
                                className="rc-tree-indent-unit"
                              />
                            ))
                          })()
                        }
                      </span>

                      <div
                        className="relative"
                        style={{ width: '200px', whiteSpace: 'nowrap', alignSelf: 'center' }}
                      >
                        <div className="flex flex-col">
                          <div className="flex h-4 items-center">
                            <Label className="mx-4">Code: </Label>
                            {code}
                          </div>

                          {
                            (!isCodeValid || !isCodeStructureValid || !isCodeUnique) && (
                              <span className="pl-4 text-xs text-red-500">
                                {(!isCodeValid && codeErrorMessage) || (!isCodeStructureValid && codeStructureErrorMessage) || (!isCodeUnique && codeUniqueErrorMessage)}
                              </span>
                            )
                          }
                        </div>
                      </div>

                      <div
                        className="flex-1 p-2"
                        style={
                          {
                            width: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }
                        }
                      >
                        Multi Tenant
                        :
                        {' '}
                        {multiTenant ? 'Yes' : 'No'}
                      </div>

                      <div
                        className="flex-1 p-2"
                        style={
                          {
                            width: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }
                        }
                      >
                        Multi Tenant Host
                        :
                        {' '}
                        {multiTenantHost ? 'Yes' : 'No'}
                      </div>
                    </div>
                  )
            }

            <IconButton
              className="ml-3 size-6 bg-transparent"
              color="muted"
              disabled={!privilegeValue?.has('0.4.0.2')}
              type="button"
              onClick={() => handleEditToggle(key)}
            >
              <Icon
                className="size-4"
                icon={editableItems[key] ? 'lucide:check' : 'lucide:edit-2'}
              />
            </IconButton>

            {
              editableItems[key] && (
                <IconButton
                  className="size-8"
                  color="error"
                  disabled={!privilegeValue?.has('0.3.8.11')}
                  type="button"
                  variant="text"
                  onClick={() => { state$.isOpenDeletePrivileges.set(true), state$.deletePrivilege.set(node) }} // Call handleDelete with the key of the node to delete
                >
                  <Icon icon="lucide:trash-2" />
                </IconButton>
              )
            }
          </div>
        </div>
      )
    },
    [
      editableItems,
      editingTitle,
      editingCode,
      multiTenant,
      multiTenantHost,
      handleDelete,
      handleEditToggle,
      handleInputChange,
      handleInputBlur,
      handleInputCodeBlur,
      handleInputCodeChange,
      handleMultiTenantChange,
    ],
  )

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold">
          Product Privileges Page
        </span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeletePrivileges.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deletePrivilege.get()?.name}' (${state$.deletePrivilege.get()?.code}) privilege?`}
                onClose={() => { state$.isOpenDeletePrivileges.set(false), state$.deletePrivilege.set(undefined) }}
                onSubmit={
                  () => {
                    const deletePrivilege = state$.deletePrivilege.get()
                    if (deletePrivilege) {
                      handleDelete(deletePrivilege)
                      state$.isOpenDeletePrivileges.set(false)
                      state$.deletePrivilege.set(undefined)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Computed>
          {
            () => (
              <Button
                className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
                color="muted"
                disabled={!privilegeValue?.has('0.3.8.9') || state$.productIds.get() === '' || state$.productIds.get() === null || state$.productIds.get() === undefined}
                onClick={
                  () => {
                    const id = ulid()
                    const newItem = {
                      key: id,
                      name: 'New Privilege', // Initialize with 'New Privilege' title
                      parentId: '',
                      children: [],
                      isNew: true,
                      code: '',
                      pos: '',
                      active: true,
                      multiTenant: false,
                      multiTenantHost: false,
                    }
                    formControl.setFieldValue('list', (prevData: any) => [...prevData, newItem])
                    setTimeout(() => {
                      const element = document.getElementById(id)
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
                        element.focus()
                      }
                    }, 0)
                    // Ensure the keys are expanded after adding new item
                    const expandedKeys = new Set(state$.expandedKeys.get())
                    expandedKeys.add(id)
                    state$.expandedKeys.set(Array.from(expandedKeys))
                  }
                }
              >
                Add New
              </Button>
            )
          }
        </Computed>

      </div>

      <div
        className="grid grid-cols-3 gap-3"
      >
        <div className="grid gap-2">
          <formControl.Field name="productId">
            {
              field => (
                <>
                  <div className="relative">
                    <Label
                      required
                      className="text-xs"
                      htmlFor="productId"
                    >
                      Products
                    </Label>

                    <Computed>
                      {
                        () => (
                          <Dropdown
                            id="productId"
                            label=""
                            options={productIds}
                            value={field.state.value}
                            onValueChange={
                              (e) => {
                                if (e !== '') {
                                  field.handleChange(e)
                                  field.validate('change')
                                  state$.productIds.set(e)
                                  queryClient.invalidateQueries({
                                    queryKey: ['add-product-privileges-listing'],
                                  })
                                }
                              }
                            }
                          />
                        )
                      }
                    </Computed>
                  </div>

                  {
                    field.state.meta.errors && (
                      <em
                        className="error-message"
                        role="alert"
                      >
                        {field.state.meta.errors.join(', ')}
                      </em>
                    )
                  }
                </>
              )
            }
          </formControl.Field>
        </div>
      </div>

      <div className="p-2">
        <form
          onSubmit={
            async (e) => {
              const fieldMeta = formControl.state.fieldMeta
              if (fieldMeta) {
                const fieldsToValidate = Object.keys(fieldMeta) as string[]
                const validationResults = await Promise.all(
                  fieldsToValidate.map(async (field) => {
                    const result = await formControl.validateField(field as any, 'change')
                    return { field, result }
                  }),
                )
              }
              e.preventDefault() // Prevent the default form submission behavior
              formControl.handleSubmit()
            }
          }
        >
          <Card className="mt-4">
            <CardContent>
              <formControl.Field name="list">
                {
                  field => (
                    <>
                      <div className="p-2">
                        <Computed>
                          {
                            () => (
                              <Tree
                                draggable={{ icon: '↕️' }}
                                expandedKeys={state$.expandedKeys.get()}
                                itemHeight={600}
                                showIcon={false}
                                titleRender={renderTitle}
                                treeData={field.state.value as TreeNode[]}
                                virtual={false}
                                // onDragEnter={onDragEnter}
                                // onDragStart={onDragStart}
                                onDrop={onDrop}
                                onExpand={onExpand}
                              />
                            )
                          }
                        </Computed>
                      </div>
                    </>
                  )
                }
              </formControl.Field>
            </CardContent>

            <CardFooter>
              <Computed>
                {
                  () => (
                    <formControl.Subscribe
                      selector={state => [state.canSubmit, state.isSubmitting]}
                    >
                      {
                        ([canSubmit, isSubmitting]) => (
                          <Button
                            fullWidth
                            disabled={!privilegeValue?.has('0.3.8.8') || !privilegeValue?.has('0.3.8.10') || state$.isSubmit.get()}
                          >
                            {isSubmitting ? '...' : 'Save'}
                          </Button>
                        )
                      }
                    </formControl.Subscribe>
                  )
                }
              </Computed>
            </CardFooter>
          </Card>
        </form>
      </div>
    </div>
  )
}
